<template>
  <transition
    name="expand"
    mode="out-in"
    @enter="onConfiguratorEnter"
    @after-enter="onConfiguratorAfterEnter"
    @leave="onConfiguratorLeave"
  >
    <slot
      :styles="
        {
          'will-change': 'height',
          transform: 'translateZ(0)',
          'backface-visibility': 'hidden',
          perspective: '1000px',
          '--app-animate-height__duration': props.duration,
        } as const
      "
    />
  </transition>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    duration?: string
  }>(),
  {
    duration: '300ms',
  },
)

const onConfiguratorEnter = (element: Element) => {
  if (element instanceof HTMLElement === false) return

  const width = getComputedStyle(element).width

  element.style.width = width
  element.style.position = 'absolute'
  element.style.visibility = 'hidden'
  element.style.height = 'auto'

  const height = getComputedStyle(element).height

  element.style.width = ''
  element.style.position = ''
  element.style.visibility = ''
  element.style.height = '0'

  // Force repaint to make sure the
  // animation is triggered correctly.
  getComputedStyle(element).height

  // Trigger the animation.
  // We use `requestAnimationFrame` because we need
  // to make sure the browser has finished
  // painting after setting the `height`
  // to `0` in the line above.
  requestAnimationFrame(() => {
    element.style.height = `max(${height}, 260px)`
  })
}

const onConfiguratorAfterEnter = (element: Element) => {
  if (element instanceof HTMLElement === false) return

  element.style.height = 'max(auto, 260px)'
}

const onConfiguratorLeave = (element: Element) => {
  if (element instanceof HTMLElement === false) return

  const height = getComputedStyle(element).height

  element.style.height = `max(${height}, 260px)`

  // Force repaint to make sure the
  // animation is triggered correctly.
  getComputedStyle(element).height

  requestAnimationFrame(() => {
    element.style.height = '0'
  })
}
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: height var(--app-animate-height__duration, 300ms)
    cubic-bezier(0.68, 0.56, 0, 1);
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}
</style>
